/**
 * GmEventManager — the 🎪 Event Control window (Event Control Round A E.3).
 *
 * ONE state-aware GM surface for the whole event workflow, keyed on the room
 * schema (`state.gmEvent` — the truth, M2) + the persistent last-race
 * results. Four mutually exclusive sections:
 *
 *   no event, no results  → CREATE: server-fetched template cards, one click
 *   announced/registration → LOBBY: start / warp / cancel + join/leave
 *   staging/live           → LIVE: next phase / end / cancel / transfer
 *   no event, results held → POST-RACE: standings + 🔁 Run it back
 *
 * There is NO event-ID input — every verb reads the schema snapshot. The old
 * separate Event Creator window was retired into this one (its one-click
 * create had been broken since day one: it sent {templateId} against a
 * definition-required validator — see EVENT_CONTROL_SPEC §1.1).
 *
 * Server enforces every verb regardless of UI (COMMAND_PERMISSIONS); buttons
 * grey out below the required tier as a courtesy.
 *
 * @see docs/design/gm/EVENT_CONTROL_SPEC.md §3.2
 */

import React, { useEffect, useRef } from 'react';
import { EventBus, EVENTS } from '../../../../shared/EventBus';
import {
  useGmEvent,
  useGmEventSchema,
  useLastEnded,
  clearLastEnded,
  type GmEventSchemaSnapshot,
} from '../../../services/GmEventService';
import { GmLevelChip, gmBtnStyle, gmSectionTitleStyle, useSelfGmLevel, selfCanRun, getRoom } from '../Gm/gmShared';
import { formatRaceTime } from './RaceResultsHud';
import { COLORS, FONTS } from '../../theme';
import { shouldRequestRoomData, ROOM_REQUEST_RETRY_MS, type RoomRequestRecord } from '../../hooks/roomRequestGate';

/** Per-mode card icon (client-side presentation; catalog is server data). */
const MODE_ICONS: Record<string, string> = { race: '🏃', fishing: '🎣' };

/** The last non-empty templateId seen on the schema — survives the event's
 *  schema clear at end so 🔁 Run it back knows what to recreate. */
let lastSeenTemplateId = '';

function formatCountdown(seconds: number): string {
  if (seconds <= 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}:${s.toString().padStart(2, '0')}` : `${s}s`;
}

export const GmEventManagerContent: React.FC = () => {
  const { scoreboard, templates, actions } = useGmEvent();
  const snap = useGmEventSchema();
  const lastEnded = useLastEnded();
  const gmLevel = useSelfGmLevel();

  const canCreate = selfCanRun('eventcreate', gmLevel);
  const canDrive = selfCanRun('eventstart', gmLevel);

  // Cache template provenance for run-it-back (module-level: survives
  // remounts within the session).
  useEffect(() => {
    if (snap?.templateId) lastSeenTemplateId = snap.templateId;
  }, [snap?.templateId]);

  // Fetch once per room/session identity while the create section needs the
  // catalog. Keep the lifecycle watcher alive after the first response: a
  // reconnect needs a fresh request even while the old catalog is displayed.
  // A persisted-open window can mount before a room exists, and a reconnect
  // can replace the room without remounting this component.
  const requestedRoomRef = useRef<RoomRequestRecord | null>(null);
  // The template list is delivered through useGmEvent, so "answered" is
  // `templates` becoming non-null. Mirrored into a ref because the request
  // loop below runs from a closure that must not re-subscribe per render.
  const templatesArrivedRef = useRef(false);
  templatesArrivedRef.current = templates != null;
  const needsCatalog = !snap;
  useEffect(() => {
    if (!needsCatalog) return;
    const requestForCurrentRoom = () => {
      const room = getRoom();
      if (!room) return;
      const sessionId = room.sessionId ?? '';
      const now = Date.now();
      const record = requestedRoomRef.current;
      // A send is not an answer: if the server handler was still rebinding when
      // the request landed, the reply never arrives and the poll below would
      // otherwise early-return forever on the matching identity.
      if (record) record.answered = templatesArrivedRef.current;
      if (!shouldRequestRoomData(record, room, sessionId, now, ROOM_REQUEST_RETRY_MS)) return;
      try {
        room.send('gmEventTemplateList', {});
        requestedRoomRef.current = { room, sessionId, sentAt: now, answered: false };
      } catch {
        // Retry on the next poll if the room disappeared during the send.
      }
    };
    const unsubscribeConnected = EventBus.getInstance().on(
      EVENTS.NET_CONNECTED,
      requestForCurrentRoom,
    );
    requestForCurrentRoom();
    const interval = setInterval(requestForCurrentRoom, 500);
    return () => {
      unsubscribeConnected();
      clearInterval(interval);
    };
  }, [needsCatalog]);

  const phase = snap?.phase ?? '';
  const inLobby = phase === 'pending' || phase === 'announced' || phase === 'registration';
  const inLive = phase === 'staging' || phase === 'live';

  return (
    <div style={{ padding: 16, color: COLORS.textPrimary, fontFamily: FONTS.ui, fontSize: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>🎪 Event Control</h3>
        <GmLevelChip gmLevel={gmLevel} />
      </div>

      {snap && inLobby && <LobbySection snap={snap} canDrive={canDrive} actions={actions} />}
      {snap && inLive && <LiveSection snap={snap} canDrive={canDrive} actions={actions} scoreboard={scoreboard} />}
      {!snap && lastEnded && (
        <PostRaceSection lastEnded={lastEnded} canCreate={canCreate} actions={actions} />
      )}
      {!snap && !lastEnded && (
        <CreateSection templates={templates} canCreate={canCreate} actions={actions} />
      )}
    </div>
  );
};

// ── Sections ─────────────────────────────────────────────────────────────

type Actions = ReturnType<typeof useGmEvent>['actions'];

const CreateSection: React.FC<{
  templates: ReturnType<typeof useGmEvent>['templates'];
  canCreate: boolean;
  actions: Actions;
}> = ({ templates, canCreate, actions }) => (
  <div>
    <div style={gmSectionTitleStyle}>Create an event</div>
    {!templates && (
      <div style={{ color: COLORS.textMuted, fontSize: 11 }}>Loading templates…</div>
    )}
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {templates?.map((tpl) => (
        <button
          key={tpl.id}
          style={{
            ...gmBtnStyle(canCreate),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 4,
            textAlign: 'left',
            padding: '10px 12px',
          }}
          disabled={!canCreate}
          onClick={() => actions.createFromTemplate(tpl.id)}
        >
          <span style={{ fontSize: 14 }}>
            {MODE_ICONS[tpl.mode] ?? '🎪'} {tpl.title}
          </span>
          <span style={{ fontSize: 10, color: COLORS.textMuted }}>{tpl.description}</span>
        </button>
      ))}
    </div>
    <div style={{ marginTop: 12, fontSize: 10, color: COLORS.textMuted }}>
      Custom definitions: /eventcreate via chat.
    </div>
  </div>
);

const LobbySection: React.FC<{ snap: GmEventSchemaSnapshot; canDrive: boolean; actions: Actions }> = ({ snap, canDrive, actions }) => {
  const selfSessionId: string = getRoom()?.sessionId ?? '';
  const selfInEvent = !!getRoom()?.state?.players?.get?.(selfSessionId)?.eventId;
  // Manual creates land in 'pending' — the primary verb there is ANNOUNCE
  // (open the lobby); joins are server-rejected until announced/registration.
  const isPending = snap.phase === 'pending';
  const canJoin = !isPending && !selfInEvent;
  return (
    <div>
      <EventHeader snap={snap} />
      <div style={gmSectionTitleStyle}>Lobby</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <button
          style={gmBtnStyle(canDrive && (isPending || snap.participantCount > 0))}
          // Starting an empty lobby just trips the staging gate's
          // "Not enough participants" cancel — refuse with a reason.
          disabled={!canDrive || (!isPending && snap.participantCount === 0)}
          title={!isPending && snap.participantCount === 0 ? 'No participants yet — players must join first' : undefined}
          onClick={() => actions.start(snap.id)}
        >
          {isPending ? '📣 Announce' : '▶ Start'}
        </button>
        <button style={gmBtnStyle(canDrive)} disabled={!canDrive} onClick={() => actions.warp(snap.id)}>
          🌀 Warp
        </button>
        <CancelButton snap={snap} canDrive={canDrive} actions={actions} />
      </div>
      <div style={gmSectionTitleStyle}>As player</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          style={gmBtnStyle(canJoin)}
          disabled={!canJoin}
          title={isPending ? 'Joins open once the event is announced' : undefined}
          onClick={() => actions.join(snap.id)}
        >
          ✅ Join
        </button>
        <button style={gmBtnStyle(selfInEvent)} disabled={!selfInEvent} onClick={() => actions.leave(snap.id)}>
          🚪 Leave
        </button>
      </div>
    </div>
  );
};

const LiveSection: React.FC<{
  snap: GmEventSchemaSnapshot;
  canDrive: boolean;
  actions: Actions;
  scoreboard: ReturnType<typeof useGmEvent>['scoreboard'];
}> = ({ snap, canDrive, actions, scoreboard }) => (
  <div>
    <EventHeader snap={snap} />
    <div style={gmSectionTitleStyle}>Live controls</div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      <button style={gmBtnStyle(canDrive)} disabled={!canDrive} onClick={() => actions.phaseAdvance(snap.id)}>
        ⏭ Next Phase
      </button>
      <button style={gmBtnStyle(canDrive)} disabled={!canDrive} onClick={() => actions.phaseAdvance(snap.id, 'finished')}>
        🏁 End
      </button>
      <CancelButton snap={snap} canDrive={canDrive} actions={actions} />
      <button
        style={gmBtnStyle(canDrive)}
        disabled={!canDrive}
        onClick={() => {
          const target = prompt('Transfer ownership to sessionId:');
          if (target) getRoom()?.send('gmEventTransfer', { eventId: snap.id, targetSessionId: target });
        }}
      >
        ↔ Transfer
      </button>
    </div>
    {scoreboard && scoreboard.entries.length > 0 && (
      <div style={{ marginTop: 12 }}>
        <div style={gmSectionTitleStyle}>Participants ({scoreboard.entries.length})</div>
        {scoreboard.entries.map((e) => (
          <div key={e.sessionId} style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{e.heroName}</span>
            <span style={{ color: COLORS.textAccent }}>{e.score} pts</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const PostRaceSection: React.FC<{
  lastEnded: NonNullable<ReturnType<typeof useLastEnded>>;
  canCreate: boolean;
  actions: Actions;
}> = ({ lastEnded, canCreate, actions }) => {
  const runbackId = lastSeenTemplateId;
  const top = lastEnded.scoreboard.entries.slice(0, 5);
  return (
    <div>
      <div style={gmSectionTitleStyle}>Last event results</div>
      {top.length === 0 && <div style={{ color: COLORS.textMuted }}>No finishers recorded.</div>}
      {top.map((e, i) => (
        <div key={e.sessionId} style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>
            {['🥇', '🥈', '🥉'][i] ?? `${i + 1}.`} {e.heroName}
          </span>
          <span style={{ color: COLORS.textAccent }}>
            {e.finishTimeMs ? formatRaceTime(e.finishTimeMs)
              : e.status === 'dnf' ? 'DNF' : `${e.score} pts`}
          </span>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        {runbackId && (
          <button
            style={gmBtnStyle(canCreate)}
            disabled={!canCreate}
            onClick={() => actions.createFromTemplate(runbackId)}
          >
            🔁 Run it back
          </button>
        )}
        <button style={gmBtnStyle(true)} onClick={() => clearLastEnded()}>
          ✕ Clear results
        </button>
      </div>
      <div style={{ marginTop: 8, fontSize: 10, color: COLORS.textMuted }}>
        Clearing results shows the template list.
      </div>
    </div>
  );
};

// ── Shared bits ──────────────────────────────────────────────────────────

const EventHeader: React.FC<{ snap: GmEventSchemaSnapshot }> = ({ snap }) => (
  <div style={{ padding: 8, background: COLORS.surfaceSunken, borderRadius: 4, marginBottom: 4 }}>
    <div style={{ fontSize: 13, fontWeight: 'bold', color: COLORS.textAccent }}>{snap.title}</div>
    <div style={{ fontSize: 11, color: COLORS.textSecondary }}>
      phase <b>{snap.phase}</b> · {snap.participantCount} joined · ⏱ {formatCountdown(snap.secondsRemaining)}
    </div>
  </div>
);

const CancelButton: React.FC<{ snap: GmEventSchemaSnapshot; canDrive: boolean; actions: Actions }> = ({ snap, canDrive, actions }) => (
  <button
    style={{ ...gmBtnStyle(canDrive), borderColor: 'rgba(231, 76, 60, 0.5)', color: canDrive ? '#e74c3c' : '#7f8c8d' }}
    disabled={!canDrive}
    onClick={() => actions.cancel(snap.id, 'Manual cancel')}
  >
    ❌ Cancel
  </button>
);
